/**
 * Shared chart scales — map data ↔ plot-local pixels.
 * Use these for both drawing and interaction (hover / scrub / hit-test).
 */

export interface LogFreqScale {
  kind: 'log-freq';
  fMin: number;
  fMax: number;
  plotW: number;
}

export interface LinearScale {
  kind: 'linear';
  lo: number;
  hi: number;
  /** When true, higher data values map toward smaller y (charts). */
  invertY: boolean;
  length: number;
}

export function createLogFreqScale(
  plotW: number,
  fMin = 20,
  fMax = 200,
): LogFreqScale {
  return { kind: 'log-freq', fMin, fMax, plotW: Math.max(0, plotW) };
}

export function createLinearScale(
  length: number,
  lo: number,
  hi: number,
  invertY = true,
): LinearScale {
  return { kind: 'linear', lo, hi, invertY, length: Math.max(0, length) };
}

export function logFreqToX(scale: LogFreqScale, f: number): number {
  const lo = Math.log10(scale.fMin);
  const hi = Math.log10(scale.fMax);
  if (!(hi > lo) || scale.plotW <= 0) return 0;
  const t = (Math.log10(Math.max(f, scale.fMin)) - lo) / (hi - lo);
  return t * scale.plotW;
}

export function plotToLogFreq(scale: LogFreqScale, x: number): number {
  const lo = Math.log10(scale.fMin);
  const hi = Math.log10(scale.fMax);
  if (!(hi > lo) || scale.plotW <= 0) return scale.fMin;
  const t = Math.min(1, Math.max(0, x / scale.plotW));
  return 10 ** (lo + t * (hi - lo));
}

export function linearToPlot(scale: LinearScale, v: number): number {
  const span = scale.hi - scale.lo;
  if (!(Math.abs(span) > 1e-12) || scale.length <= 0) return 0;
  const t = (v - scale.lo) / span;
  const clamped = Math.min(1, Math.max(0, t));
  return scale.invertY ? (1 - clamped) * scale.length : clamped * scale.length;
}

export function plotToLinear(scale: LinearScale, p: number): number {
  if (scale.length <= 0) return scale.lo;
  const t = Math.min(1, Math.max(0, p / scale.length));
  const u = scale.invertY ? 1 - t : t;
  return scale.lo + u * (scale.hi - scale.lo);
}

/** Expand [min,max] by a fraction of the span (or `fallbackPad` if span≈0). */
export function padDomain(
  lo: number,
  hi: number,
  fraction = 0.1,
  fallbackPad = 5,
): { lo: number; hi: number } {
  const span = hi - lo;
  const pad = (Math.abs(span) > 1e-12 ? span : fallbackPad) * fraction;
  return { lo: lo - Math.abs(pad), hi: hi + Math.abs(pad) };
}
